/**
 * Blacklisted slugs that cannot be used as league/organization names.
 * These are reserved for system use, common website pages, or could cause conflicts.
 */

export const BLACKLISTED_SLUGS: string[] = [
  // Authentication & Account
  "login",
  "logout",
  "log-in",
  "log-out",
  "signin",
  "signout",
  "sign-in",
  "sign-out",
  "sign",
  "signup",
  "sign-up",
  "register",
  "auth",
  "authenticate",
  "account",
  "accounts",
  "profile",
  "profiles",
  "settings",
  "preferences",
  "password",
  "reset",
  "verify",
  "confirm",
  "activate",
  "deactivate",
  "unsubscribe",

  // Technical & Infrastructure
  "admin",
  "administrator",
  "api",
  "cdn",
  "mail",
  "email",
  "smtp",
  "ftp",
  "sftp",
  "static",
  "assets",
  "images",
  "img",
  "files",
  "media",
  "uploads",
  "upload",
  "download",
  "downloads",
  "status",
  "health",
  "ping",
  "test",
  "dev",
  "development",
  "staging",
  "sandbox",
  "localhost",
  "local",
  "production",
  "prod",
  "server",
  "servers",
  "node",
  "cluster",
  "cache",
  "redis",
  "database",
  "db",
  "ssl",
  "secure",
  "webhook",
  "webhooks",
  "callback",
  "oauth",
  "sso",

  // Common Website Pages
  "www",
  "home",
  "index",
  "about",
  "about-us",
  "contact",
  "contact-us",
  "help",
  "support",
  "faq",
  "faqs",
  "terms",
  "terms-of-service",
  "tos",
  "privacy",
  "privacy-policy",
  "legal",
  "dmca",
  "copyright",
  "trademark",
  "policy",
  "policies",
  "guidelines",
  "rules",
  "blog",
  "news",
  "press",
  "media",
  "announcements",
  "updates",
  "changelog",
  "release",
  "releases",
  "careers",
  "jobs",
  "hiring",
  "pricing",
  "plans",
  "features",
  "demo",
  "tour",
  "try",
  "get-started",
  "getting-started",

  // Business & Commerce
  "enterprise",
  "business",
  "pro",
  "premium",
  "free",
  "trial",
  "subscribe",
  "subscription",
  "checkout",
  "payment",
  "payments",
  "billing",
  "invoice",
  "invoices",
  "store",
  "shop",
  "marketplace",
  "merch",
  "merchandise",
  "gear",
  "apparel",

  // Platform & App
  "app",
  "apps",
  "application",
  "mobile",
  "ios",
  "android",
  "web",
  "site",
  "website",
  "dashboard",
  "console",
  "portal",
  "panel",
  "official",
  "staty",
  "staty-io",
  "statyapps",
  "staty-apps",
  "statyapps-io",

  // Sports (platform-supported sports)
  "basketball",
  "soccer",
  "football",
  "volleyball",
  "pickleball",
  "softball",
  "baseball",
  "golf",
  "tennis",
  "hockey",
  "lacrosse",
  "rugby",
  "cricket",
  "badminton",
  "swimming",
  "track",
  "wrestling",
  "boxing",
  "mma",
  "esports",
  "sports",
  "sport",

  // Platform Entity Names
  "league",
  "leagues",
  "tournament",
  "tournaments",
  "team",
  "teams",
  "player",
  "players",
  "game",
  "games",
  "match",
  "matches",
  "season",
  "seasons",
  "division",
  "divisions",
  "registration",
  "registrations",
  "schedule",
  "schedules",
  "standings",
  "stats",
  "statistics",
  "scores",
  "scoreboard",
  "bracket",
  "brackets",
  "playoff",
  "playoffs",
  "roster",
  "rosters",

  // Actions & CRUD
  "create",
  "new",
  "edit",
  "update",
  "delete",
  "remove",
  "manage",
  "add",
  "view",
  "list",
  "search",
  "find",
  "browse",
  "explore",

  // Community & Social
  "community",
  "communities",
  "forum",
  "forums",
  "chat",
  "discord",
  "slack",
  "social",
  "share",
  "invite",
  "invites",
  "referral",
  "referrals",
  "member",
  "members",
  "user",
  "users",

  // Documentation & Resources
  "docs",
  "documentation",
  "resources",
  "resource",
  "guide",
  "guides",
  "tutorial",
  "tutorials",
  "learn",
  "wiki",
  "knowledge",
  "kb",
  "knowledgebase",

  // Feedback & Testing
  "feedback",
  "survey",
  "surveys",
  "beta",
  "alpha",
  "internal",
  "external",
  "public",
  "private",

  // Error & System
  "null",
  "undefined",
  "error",
  "errors",
  "404",
  "500",
  "notfound",
  "not-found",

  // Too Short or Generic
  "a",
  "b",
  "c",
  "d",
  "e",
  "f",
  "g",
  "h",
  "i",
  "j",
  "k",
  "l",
  "m",
  "n",
  "o",
  "p",
  "q",
  "r",
  "s",
  "t",
  "u",
  "v",
  "w",
  "x",
  "y",
  "z",
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "me",
  "my",
  "the",
  "all",
  "any",
  "no",
  "yes",
  "ok",
  "hi",
  "hey",
];

/**
 * Check if a slug is blacklisted
 */
export const isSlugBlacklisted = (slug: string): boolean => {
  if (!slug) return false;
  const normalized = slug.toLowerCase().trim();
  return BLACKLISTED_SLUGS.includes(normalized);
};

export default BLACKLISTED_SLUGS;
